import React, { useState } from 'react';
import { Box, Button, MenuItem, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import FinMark from '../../brand/FinMark';
import { tokens } from '../../../theme/tokens';
import { currencyOptions, languageOptions, OnboardingPreferences, timezoneOptions } from '../preferenceOptions';
import { Label, Lead, Num } from './type';

interface Props {
  titleId: string;
  firstName?: string;
  preferences: OnboardingPreferences;
  onPreferencesChange: (preferences: OnboardingPreferences) => void;
}

/**
 * Fin says hello and how it starts: Telegram on the next screen, then the
 * first steps on Início. The preferences fit in one line.
 */
const WelcomeStep: React.FC<Props> = ({ titleId, firstName, preferences, onPreferencesChange }) => {
  const { t } = useTranslation('onboarding');
  const [editing, setEditing] = useState(false);
  const currencies = currencyOptions(t);
  const languages = languageOptions(t);
  const zones = timezoneOptions(t, preferences.timezone);
  const labelOf = (options: Array<{ value: string; label: string }>, value: string) =>
    options.find((option) => option.value === value)?.label ?? value;

  const change = (field: keyof OnboardingPreferences) => (event: React.ChangeEvent<HTMLInputElement>) =>
    onPreferencesChange({ ...preferences, [field]: event.target.value });

  const plan = [
    { title: t('welcome.plan.link.title'), text: t('welcome.plan.link.text') },
    { title: t('welcome.plan.firstSteps.title'), text: t('welcome.plan.firstSteps.text') },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2.5, md: 3.25 } }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { sm: 'center' }, gap: { xs: 2, sm: 2.5 } }}>
        <FinMark size={68} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Box component='h2' id={titleId} sx={{ m: 0, fontFamily: tokens.display, fontSize: { xs: 28, md: 34 }, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.15 }}>
            {firstName ? t('welcome.titleWithName', { name: firstName }) : t('welcome.title')}
          </Box>
          <Lead>{t('welcome.lead')}</Lead>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Label>{t('welcome.planLabel')}</Label>
        <Box component='ol' sx={{ m: 0, p: 0, listStyle: 'none', display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: { xs: 1.5, md: 1.75 } }}>
          {plan.map((item, index) => (
            <Box
              component='li'
              key={item.title}
              sx={{
                p: { xs: 0, md: 2.25 },
                borderRadius: '18px',
                bgcolor: { xs: 'transparent', md: tokens.bg },
                display: 'flex',
                flexDirection: { xs: 'row', md: 'column' },
                alignItems: { xs: 'flex-start', md: 'stretch' },
                gap: { xs: 1.5, md: 1 },
              }}
            >
              <Num>{index + 1}</Num>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <strong>{item.title}</strong>
                <Box component='span' sx={{ fontSize: 14, color: tokens.ink3 }}>{item.text}</Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ border: `1px solid ${tokens.line}`, borderRadius: '18px', px: 2.25, py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
            <Box component='strong' sx={{ fontSize: 14 }}>{t('welcome.prefsTitle')}</Box>
            <Box component='span' sx={{ fontSize: 14, color: tokens.ink3 }}>
              {[labelOf(currencies, preferences.defaultCurrency), labelOf(languages, preferences.language), labelOf(zones, preferences.timezone)].join(' · ')}
            </Box>
          </Box>
          <Button variant='outlined' onClick={() => setEditing((open) => !open)} aria-expanded={editing} sx={{ minHeight: 40, flexShrink: 0 }}>
            {editing ? t('welcome.prefsDone') : t('welcome.prefsChange')}
          </Button>
        </Box>
        {editing && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5 }}>
            <TextField select size='small' label={t('preferences.defaultCurrency')} value={preferences.defaultCurrency} onChange={change('defaultCurrency')}>
              {currencies.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
            </TextField>
            <TextField select size='small' label={t('preferences.language')} value={preferences.language} onChange={change('language')}>
              {languages.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
            </TextField>
            <TextField select size='small' label={t('preferences.timezone')} value={preferences.timezone} onChange={change('timezone')}>
              {zones.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
            </TextField>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default WelcomeStep;
